import React, { useMemo } from 'react';
import { Box } from '@mui/material';

import { useGetNearbyBuoysQuery } from 'actions/buoys';
import BuoyCarousel from 'components/BuoyCarousel';

import NearbyBuoysHeader from './components/NearbyBuoysHeader';

import styles from './NearbyBuoys.module.scss';

interface Props {
  buoyId?: string;
  loading?: boolean;
  error?: boolean | string | Error;
  location?: {
    latitude: number;
    longitude: number;
  };
  units?: {
    swellHeight: string;
  };
  trackLocation?: string;
  spotId?: string;
  spotName?: string;
}

const NearbyBuoys: React.FC<Props> = ({
  buoyId,
  loading,
  location,
  units,
  error,
  trackLocation,
  spotId,
  spotName,
}) => {
  const skip = !!(loading || error || !location || !units);

  const nearbyBuoysParams =
    location && units ? { ...location, ...units, limit: 10, distance: 200 } : undefined;

  const {
    data: nearbyBuoys,
    error: nearbyError,
    isFetching: nearbyFetching,
    isUninitialized: nearbyUninitialized,
  } = useGetNearbyBuoysQuery(nearbyBuoysParams!, { skip });

  const nearbyBuoysWithoutCurrentBuoy = useMemo(() => {
    if (nearbyBuoys?.data) {
      return nearbyBuoys.data.filter((nearbyBuoy) => nearbyBuoy.id !== buoyId);
    }
    return null;
  }, [nearbyBuoys, buoyId]);

  const nearbyLoading = nearbyFetching || nearbyUninitialized;

  if (nearbyBuoysWithoutCurrentBuoy?.length === 0) {
    return null;
  }

  return (
    <Box className={styles.nearbyBuoys}>
      <NearbyBuoysHeader
        loading={loading}
        latitude={location?.latitude}
        longitude={location?.longitude}
        trackLocation={trackLocation}
        spotId={spotId}
        spotName={spotName}
      />
      <div className="sl-section-full-bleed">
        <BuoyCarousel
          loading={nearbyLoading}
          error={error || (nearbyError as Error)}
          stations={nearbyBuoysWithoutCurrentBuoy || []}
          trackLocation={trackLocation}
        />
      </div>
    </Box>
  );
};

export default NearbyBuoys;
